import React, { FC, useState } from 'react';
import { CompanyAWS } from '../../../@types/AWS';
import Modal from '../../common/Modal/Modal';
import { CompanyAttributes, License, LicenseStatusTypes, LicenseTypes } from '../../../@types/Company';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import Shape from '../../../@types/Shape';
import Form from '../../common/Form/Form';
import { FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import Button from '../../common/Button';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import { StateFieldAutoComplete } from '../../common/Form/FormFields/Autocomplete/Autocomplete';
import DatePicker from '../../common/Form/FormFields/DatePicker/DatePicker';
import SelectOptions from '../../common/Form/FormFields/Select/Select';
import USStates from '../../../data/USStates';
import SelectOption from '../../../@types/SelectOption';
import axios from '../../../api/axios';
import flatten from 'flat';

interface EditLicenseModalProps {
  company: CompanyAWS;
  license: License | undefined;
  index: number;
  closeModal: () => void;
  isVisible: boolean;
}

const EditLicenseModal: FC<EditLicenseModalProps> = ({ company, license, index, closeModal, isVisible }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (editedLicense: License) => {
    try {
      setLoading(true);
      const licenses: License[] = [...company.attributes.licenses ?? []];
      licenses[index] = editedLicense;
      const updatedAttributesList: CompanyAttributes = { ...company.attributes, licenses: licenses };
      const updatedCompany: CompanyAWS = { ...company, attributes: updatedAttributesList };
      await axios.put('/dealer/meta-data', updatedCompany);
      toast.success('License updated!');
      closeModal();
    } catch (error: any) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  }

  const editLicenseSchema = yup.object<Shape<License>>({
    status: yup.string().required(),
    state: yup.string().required(),
    stateCode: yup.string().required(),
    type: yup.string().required(),
    expirationDate: yup.string().required()
  });

  const getLicenseOptions = (): SelectOption[] => {
    const options: SelectOption[] = [];
    LicenseTypes.forEach((licenseType) => {
      options.push({ label: `${licenseType} License`, value: licenseType });
    });
    return options;
  }

  const getLicenseStatusOptions = (): SelectOption[] => {
    const options: SelectOption[] = [];
    LicenseStatusTypes.forEach((type) => {
      options.push({ label: `${type.toLocaleUpperCase()}`, value: type });
    });
    return options;
  }

  return (
    <>
      {isVisible &&
        <Modal title={`Edit ${license?.type} Licenses`} closeModal={closeModal}>
          <Form onSubmit={onSubmit} onError={onError} resolver={yupResolver(editLicenseSchema)} keepValuesOnReset={true} defaultValues={license}>
            {(methods) => (
              <FormProvider {...methods}>
                <Stack direction="column" spacing={2}>
                  <SelectOptions name={'status'} label="Status" options={getLicenseStatusOptions()} />
                  <StateFieldAutoComplete options={USStates} name={`state`} label='State' allowUserInput={true} />
                  <SelectOptions name={`type`} label="Type" options={getLicenseOptions()} />
                  <DatePicker name={`expirationDate`} label="Expiration Date" />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <LoadingButton loading={loading} type="submit">Submit</LoadingButton>
                    <Button onClick={closeModal} type="button">Cancel</Button>
                  </Stack>
                </Stack>
              </FormProvider>
            )}
          </Form>
        </Modal>
      }
    </>
  )
}

export default EditLicenseModal;
